import { useState } from "react"
import { Bell, Plus, Trash2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useAddReminder, useRemoveReminder } from "@/hooks/use-tasks"
import { toast } from "sonner"
import { formatDate } from "@/lib/utils"
import type { TaskReminder } from "@/lib/types"

const OFFSET_OPTIONS = [
  { value: "at_time", label: "Dokładnie o czasie" },
  { value: "5_min", label: "5 minut przed" },
  { value: "15_min", label: "15 minut przed" },
  { value: "30_min", label: "30 minut przed" },
  { value: "1_hour", label: "1 godzina przed" },
  { value: "2_hours", label: "2 godziny przed" },
  { value: "1_day", label: "1 dzień przed" },
  { value: "2_days", label: "2 dni przed" },
  { value: "1_week", label: "1 tydzień przed" },
]

const DELIVERY_OPTIONS = [
  { value: "in_app", label: "W aplikacji" },
  { value: "email", label: "Email" },
  { value: "both", label: "Oba" },
]

function getOffsetLabel(value: string): string {
  return OFFSET_OPTIONS.find(o => o.value === value)?.label ?? value
}

function getDeliveryLabel(value: string): string {
  return DELIVERY_OPTIONS.find(o => o.value === value)?.label ?? value
}

interface ReminderEditorProps {
  taskId: string
  dueDate: string | null
  dueTime: string | null
  reminders: TaskReminder[]
}

export function ReminderEditor({ taskId, dueDate, dueTime, reminders }: ReminderEditorProps) {
  const [showAdd, setShowAdd] = useState(false)
  const [offsetType, setOffsetType] = useState("15_min")
  const [deliveryMethod, setDeliveryMethod] = useState("in_app")
  const [customDate, setCustomDate] = useState("")
  const [customTime, setCustomTime] = useState("")

  const addMutation = useAddReminder()
  const removeMutation = useRemoveReminder()

  const computeRemindAt = (): string | null => {
    if (offsetType === "custom") {
      if (!customDate) return null
      return `${customDate}T${customTime || "09:00"}:00`
    }

    if (!dueDate) return null

    const dueDatetime = new Date(`${dueDate}T${dueTime || "09:00"}:00`)
    const offsets: Record<string, number> = {
      at_time: 0,
      "5_min": 5 * 60 * 1000,
      "15_min": 15 * 60 * 1000,
      "30_min": 30 * 60 * 1000,
      "1_hour": 60 * 60 * 1000,
      "2_hours": 2 * 60 * 60 * 1000,
      "1_day": 24 * 60 * 60 * 1000,
      "2_days": 2 * 24 * 60 * 60 * 1000,
      "1_week": 7 * 24 * 60 * 60 * 1000,
    }

    const offsetMs = offsets[offsetType] ?? 0
    const remindAt = new Date(dueDatetime.getTime() - offsetMs)
    return remindAt.toISOString()
  }

  const handleAdd = async () => {
    const remindAt = computeRemindAt()
    if (!remindAt) {
      toast.error("Nie udało się obliczyć daty przypomnienia")
      return
    }

    try {
      await addMutation.mutateAsync({
        taskId,
        data: {
          remind_at: remindAt,
          offset_type: offsetType,
          delivery_method: deliveryMethod,
        },
      })
      toast.success("Przypomnienie dodane")
      setShowAdd(false)
    } catch {
      toast.error("Nie udało się dodać przypomnienia")
    }
  }

  const handleRemove = async (reminderId: string) => {
    try {
      await removeMutation.mutateAsync({ taskId, reminderId })
      toast.success("Przypomnienie usunięte")
    } catch {
      toast.error("Nie udało się usunąć przypomnienia")
    }
  }

  const inputClass = "h-8 rounded-md border bg-transparent px-2 text-sm outline-none"

  return (
    <div className="space-y-3">
      {/* Existing reminders */}
      {reminders.length > 0 ? (
        <div className="space-y-2">
          {reminders.map((reminder) => (
            <div
              key={reminder.id}
              className="flex items-center justify-between rounded-lg border border-border px-3 py-2"
            >
              <div className="flex items-center gap-2">
                <Bell size={14} className={reminder.is_sent ? "text-green-500" : "text-primary"} />
                <div>
                  <p className="text-sm text-foreground">
                    {getOffsetLabel(reminder.offset_type)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDate(reminder.remind_at)} · {getDeliveryLabel(reminder.delivery_method)}
                    {reminder.is_sent && " · Wysłane"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleRemove(reminder.id)}
                className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        !showAdd && (
          <p className="text-sm text-muted-foreground">
            Brak przypomnień
          </p>
        )
      )}

      {/* Add reminder form */}
      {showAdd ? (
        <div className="space-y-3 rounded-lg border border-border p-3">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Kiedy przypomnieć
            </label>
            <select
              value={offsetType}
              onChange={(e) => setOffsetType(e.target.value)}
              className={inputClass + " w-full border-input text-foreground"}
            >
              {OFFSET_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
              <option value="custom">Niestandardowa data</option>
            </select>
          </div>

          {offsetType === "custom" && (
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                className={inputClass + " w-full border-input text-foreground"}
              />
              <input
                type="time"
                value={customTime}
                onChange={(e) => setCustomTime(e.target.value)}
                className={inputClass + " w-full border-input text-foreground"}
              />
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">
              Metoda dostarczenia
            </label>
            <select
              value={deliveryMethod}
              onChange={(e) => setDeliveryMethod(e.target.value)}
              className={inputClass + " w-full border-input text-foreground"}
            >
              {DELIVERY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {!dueDate && offsetType !== "custom" && (
            <p className="text-xs text-amber-500">
              Zadanie nie ma ustawionego terminu. Ustaw termin lub wybierz niestandardową datę.
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowAdd(false)}>
              Anuluj
            </Button>
            <Button
              size="sm"
              onClick={handleAdd}
              disabled={addMutation.isPending || (!dueDate && offsetType !== "custom")}
            >
              Dodaj
            </Button>
          </div>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setShowAdd(true)} className="w-full">
          <Plus size={14} /> Dodaj przypomnienie
        </Button>
      )}
    </div>
  )
}
